// The Templates page (issue 44): every template with a thumbnail drawn by the one renderer (sample
// content), its format, size and status (Built-in, Draft, Ready); make a new one, edit, duplicate (a
// built-in duplicates into your own Draft), delete your own. Editing happens in the same editor as an
// item, on a template instead (Editor.tsx, source kind 'template').
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Empty, ErrorMsg, Loading, Modal, PageHead } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { Brand, ContentFormat, TemplateDetail, TemplateRow } from '../../lib/contentTypes';
import { FORMAT_LABEL, useContentMeta } from './common';
import { TemplateStatus } from './Editor';
import { ensureFonts } from './fonts';
import { loadImage, renderArtboard, type RenderEnv } from './render';

const NEW_FORMATS: ContentFormat[] = ['single', 'poster', 'carousel'];
const THUMB_W = 168;

/** Brand, bundled fonts and logo versions, as a render environment for sample drawings (no images:
 *  an image box is drawn as an "Image N" placeholder). Null until everything is loaded. */
function useSampleEnv(): { env: RenderEnv | null; error: string } {
  const [brand, setBrand] = useState<Brand | null>(null);
  const [logos, setLogos] = useState<Map<string, HTMLImageElement> | null>(null);
  const [fonts, setFonts] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api<Brand>('/api/content/brand').then(setBrand, (e) => setError(errorText(e)));
    ensureFonts().then(() => setFonts(true), (e) => setError(`fonts could not be loaded: ${errorText(e)}`));
  }, []);
  useEffect(() => {
    if (!brand) return;
    let alive = true;
    Promise.all(brand.logos.map((v) => loadImage(v.asset.urls.work).then((img) => [v.asset_id, img] as const, () => null))).then((rows) => {
      if (alive) setLogos(new Map(rows.filter((r): r is readonly [string, HTMLImageElement] => r !== null)));
    });
    return () => {
      alive = false;
    };
  }, [brand]);
  const env = useMemo<RenderEnv | null>(() => {
    if (!brand || !logos || !fonts) return null;
    const logo = brand.logo_asset_id ? (logos.get(brand.logo_asset_id) ?? null) : null;
    return { brand: brand.spec, images: new Map(), usage: new Map(), logo, logos, placeholders: true };
  }, [brand, logos, fonts]);
  return { env, error };
}

function Thumb({ t, env }: { t: TemplateRow; env: RenderEnv }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const sample = t.sample;
  useEffect(() => {
    const c = ref.current;
    if (!c || !sample) return;
    const dpr = window.devicePixelRatio || 1;
    const s = (THUMB_W * dpr) / sample.width;
    c.width = Math.round(sample.width * s);
    c.height = Math.round(sample.height * s);
    const ctx = c.getContext('2d')!;
    ctx.setTransform(s, 0, 0, s, 0, 0);
    renderArtboard(ctx, sample, sample.artboards[0], 0, env);
  }, [sample, env]);
  if (!sample) return null;
  return <canvas ref={ref} className="cs-tpl-thumb" style={{ width: THUMB_W, height: (THUMB_W * sample.height) / sample.width }} role="img" aria-label={`Sample of ${t.name}`} />;
}

export function TemplatesPage() {
  const navigate = useNavigate();
  const { env, error: envError } = useSampleEnv();
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<TemplateRow | null>(null);
  const [busy, setBusy] = useState('');

  const load = () => api<{ items: TemplateRow[] }>('/api/content/templates?samples=true').then((d) => setRows(d.items), (e) => setError(errorText(e)));
  useEffect(() => {
    void load();
  }, []);

  async function duplicate(t: TemplateRow) {
    setBusy(t.id);
    setError('');
    try {
      const copy = await api<TemplateDetail>(`/api/content/templates/${t.id}/duplicate`, { method: 'POST' });
      navigate(`/content/templates/${copy.id}`);
    } catch (e) {
      setError(errorText(e));
      setBusy('');
    }
  }

  return (
    <div className="page">
      <p className="crumbs small">
        <Link to="/content">Content Studio</Link>
      </p>
      <PageHead title="Templates" meta="Layouts that “New content item” starts from. Built-in ones stay as shipped; yours are Drafts until you publish them.">
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          New template
        </button>
      </PageHead>
      <ErrorMsg>{error || envError}</ErrorMsg>
      {!rows || !env ? (
        <Loading what="Loading the templates" />
      ) : rows.length === 0 ? (
        <Empty>No templates yet.</Empty>
      ) : (
        <ul className="plain cs-tpl-grid" aria-label="Templates">
          {rows.map((t) => (
            <li key={t.id} className="cs-tpl-card" data-template={t.id} data-name={t.name}>
              <Link to={`/content/templates/${t.id}`} className="cs-tpl-thumb-link" aria-label={`Open ${t.name}`}>
                <Thumb t={t} env={env} />
              </Link>
              <div className="cs-tpl-info">
                <strong className="cs-tpl-name">{t.name}</strong>
                <span className="small muted">
                  {FORMAT_LABEL[t.format]} · {t.sample ? `${t.sample.width}×${t.sample.height}` : t.preset}
                  {t.sample && t.sample.artboards.length > 1 ? ` · ${t.sample.artboards.length} slides` : ''}
                </span>
                <span className="row gap-s wrap center">
                  <TemplateStatus t={t} />
                  {t.unpublished_changes && <span className="badge tone-warn">unpublished changes</span>}
                </span>
                {t.description && <span className="small muted">{t.description}</span>}
                <span className="row gap-s wrap">
                  <Link to={`/content/templates/${t.id}`} className="btn small">
                    {t.is_builtin ? 'View' : 'Edit'}
                  </Link>
                  <button type="button" className="btn small" disabled={busy === t.id} onClick={() => void duplicate(t)}>
                    Duplicate
                  </button>
                  {!t.is_builtin && (
                    <button type="button" className="btn ghost small" onClick={() => setDeleting(t)}>
                      Delete
                    </button>
                  )}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {creating && <NewTemplateDialog onClose={() => setCreating(false)} />}
      {deleting && (
        <DeleteDialog
          t={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function NewTemplateDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const meta = useContentMeta();
  const [f, setF] = useState({ name: '', format: 'poster' as ContentFormat, preset: 'portrait' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const t = await api<TemplateDetail>('/api/content/templates/new', { method: 'POST', body: f });
      navigate(`/content/templates/${t.id}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Modal
      title="New template"
      onClose={onClose}
      footer={
        <button type="submit" form="new-template" className="btn primary" disabled={busy || !f.name.trim()}>
          Create and open the editor
        </button>
      }
    >
      <form id="new-template" className="stack" onSubmit={submit} aria-label="New template">
        <label className="field">
          <span>Name</span>
          <input value={f.name} onChange={(ev) => setF({ ...f, name: ev.target.value })} required autoFocus />
        </label>
        <label className="field">
          <span>Format</span>
          <select value={f.format} onChange={(ev) => setF({ ...f, format: ev.target.value as ContentFormat })} aria-label="Template format">
            {NEW_FORMATS.map((x) => (
              <option key={x} value={x}>
                {FORMAT_LABEL[x]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Size</span>
          <select value={f.preset} onChange={(ev) => setF({ ...f, preset: ev.target.value })} aria-label="Template size">
            {(meta?.presets ?? [{ key: 'portrait', width: 1080, height: 1350 }]).map((p) => (
              <option key={p.key} value={p.key}>
                {p.key} {p.width}×{p.height}
              </option>
            ))}
          </select>
        </label>
        <p className="small muted">The size is what you draw at; an item can still be made at another size, the layout scales with it. A new template starts empty and stays a Draft until you publish it.</p>
        <ErrorMsg>{error}</ErrorMsg>
      </form>
    </Modal>
  );
}

function DeleteDialog({ t, onClose, onDeleted }: { t: TemplateRow; onClose: () => void; onDeleted: () => void }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api(`/api/content/templates/${t.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Delete this template?"
      onClose={onClose}
      footer={
        <button type="button" className="btn primary" disabled={busy} onClick={() => void go()}>
          Delete template
        </button>
      }
    >
      <p>
        “{t.name}” will be removed from the list and from “New content item”. Items already made from it keep their layout.
      </p>
      <ErrorMsg>{error}</ErrorMsg>
    </Modal>
  );
}
